import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Input, InputSearch, Range, TaptapList } from 'sonata-react-components';
import { normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { DigitalAdExchangeCatalog, DOOHAdExchangeCatalog } from 'sonata-static-storage/lib/samples/adExchanges.samples';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import { capitalize } from 'taptap-tools/utils';

import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { equalizeAdExchangesDistribution, getFlightsTotalGoals } from '../../helpers/helpers';
import { getAllowedAdExchangeCodes } from '../helpers/getAllowedAdExchangeCodes.helper';

import './AdExchangeDistribuiton.scss';

import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { GoalInterface } from 'sonata-static-storage/lib/schemas/campaign/goal.schema';
import type { AdExchangeType } from 'sonata-static-storage/lib/schemas/targeting/AdExchange/AdExchange.types';

type GoalsTotal = `${GoalInterface['type']}_overall` | `${GoalInterface['type']}_daily`;

type TableData = {
  distribution: number;
  id: string;
  name: string;
} & {
  [key in GoalsTotal]: number;
};

type Sort = {
  direction: 1 | -1;
  field: keyof TableData;
};

const sortIcons = {
  '-1': 'fa-duotone fa-solid fa-sort',
  1: 'fa-duotone fa-solid fa-flip-vertical fa-sort',
  default: 'fa-solid fa-sort'
};

type State = {
  filter?: string;
  withAutoEqualization: boolean;
} & Sort;

const getTableData = (
  adExchanges: AdExchangeType[],
  sort: Sort,
  goals: {
    daily: number;
    name: GoalInterface['type'];
    overall: number;
  }[],
  familyType: CampaignFamilyEnum
): TableData[] => {
  const adExchangeCatalog = familyType === 'dooh' ? DOOHAdExchangeCatalog : DigitalAdExchangeCatalog;
  const ads = adExchanges
    .filter(adX => !!adExchangeCatalog[adX.name])
    .map(adX => ({
      distribution: adX.distribution,
      id: adX.name,
      name: adExchangeCatalog[adX.name]!.name,
      ...goals.reduce((columns, goal) => {
        columns[`${goal.name}_overall`] = (goal.overall * adX.distribution) / 100;
        columns[`${goal.name}_daily`] = (goal.daily * adX.distribution) / 100;
        return columns;
      }, {} as any)
    }));

  return sort ? ads.sort((adA, adB) => (adA[sort.field] > adB[sort.field] ? -1 : 1) * sort.direction) : ads;
};

type Props = {
  baseCurrency: BaseCurrencyInterface;
  flightsGoals: GoalInterface[];
};

export function AdExchangeDistribuiton({ baseCurrency, flightsGoals }: Props) {
  const { formatMessage } = useIntl();
  const {
    familyType,
    setProp,
    targeting: { adExchanges },
    user
  } = useTargetingContext();

  const goalsTotal = getFlightsTotalGoals(flightsGoals);

  const [{ direction, field, filter, withAutoEqualization }, setState] = useState<State>({
    direction: -1,
    field: 'name',
    withAutoEqualization: true
  });

  const allowedAdExchangeCodes = getAllowedAdExchangeCodes(user, familyType);

  const availableAds = adExchanges.filter(
    ad =>
      allowedAdExchangeCodes.includes(ad.name) &&
      !ad.blacklisted &&
      (filter ? ad.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()) : true)
  );

  const tableRows = getTableData(
    availableAds,
    {
      direction,
      field
    },
    goalsTotal,
    familyType!
  );

  const mergeAllowedAdExchanges = (updatedAllowedAds: AdExchangeType[]) => {
    const updatedByName = new Map(updatedAllowedAds.map(ad => [ad.name, ad]));
    return adExchanges.map(ad => updatedByName.get(ad.name) ?? ad);
  };

  const handleDistribution = (adName: string) => (updatedDistribution: number) => {
    if (!withAutoEqualization) {
      setProp(
        'adExchanges',
        adExchanges.map(ad => ({
          ...ad,
          distribution: adName === ad.name ? updatedDistribution : ad.distribution
        }))
      );
      return;
    }
    const allowedAds = adExchanges.filter(ad => allowedAdExchangeCodes.includes(ad.name));
    setProp(
      'adExchanges',
      mergeAllowedAdExchanges(
        equalizeAdExchangesDistribution(allowedAds, {
          distribution: updatedDistribution,
          name: adName
        })
      )
    );
  };

  const handleSort = (sortField: keyof TableData) =>
    setState(prev => ({
      ...prev,
      direction: sortField === prev.field ? (prev.direction === 1 ? -1 : 1) : 1,
      field: sortField
    }));

  const handleFilter = (filterText: string) =>
    setState(prev => ({
      ...prev,
      filter: filterText
    }));

  const handleReset = () =>
    setProp(
      'adExchanges',
      adExchanges.map(ad => (allowedAdExchangeCodes.includes(ad.name) ? { ...ad, distribution: 0 } : ad))
    );

  const getSortIcon = (fieldSort: keyof TableData) => sortIcons[field === fieldSort ? direction : 'default'];

  const toggleAutoEqualization = () => {
    setState(prev => {
      const toggledEqualization = !prev.withAutoEqualization;
      if (toggledEqualization) {
        const allowedAds = adExchanges.filter(ad => allowedAdExchangeCodes.includes(ad.name));
        setProp('adExchanges', mergeAllowedAdExchanges(equalizeAdExchangesDistribution(allowedAds)));
      }
      return {
        ...prev,
        withAutoEqualization: toggledEqualization
      };
    });
  };

  const goalColumnsCount = goalsTotal.reduce((acc, goal) => acc + (goal.overall ? 1 : 0) + (goal.daily ? 1 : 0), 0);

  const gridCols = `4fr 80px${goalColumnsCount > 0 ? ` repeat(${goalColumnsCount}, 1fr)` : ''}`;

  return (
    <div className="adExchangeDistribution" style={{ '--adex-grid-cols': gridCols } as React.CSSProperties}>
      <div className="adExchangeDistribution__filter">
        <div className="adExchangeDistribution__filterTitle">
          {formatMessage({
            defaultMessage: 'SOURCES DISTRIBUTION',
            id: 'targeting.supplySource.distributionFilter.title'
          })}
          <ButtonUI
            icon={withAutoEqualization ? 'fa-solid fa-lock' : 'fa-regular fa-lock-open'}
            onClick={toggleAutoEqualization}
            shape="link"
            size="small"
            text={formatMessage({
              defaultMessage: `Auto Set ${withAutoEqualization.toString()}`,
              id: `targeting.supplySource.autoSet.${withAutoEqualization.toString()}`
            })}
            type="secondary"
          />
        </div>
        <InputSearch
          className="adExchangeDistribution__filterInput"
          id={'adExchange-filter'}
          lens
          name="adExchange-filter"
          onChange={(value: string) => handleFilter(value)}
          placeholder={formatMessage({
            defaultMessage: 'Search by name',
            id: 'targeting.supplySource.distributionFilter.placeholder'
          })}
          tiny
        />
        <ButtonUI
          icon="fa-solid fa-rotate-left"
          onClick={handleReset}
          size="ultraSmall"
          text={formatMessage({
            defaultMessage: 'Reset distribution',
            id: 'targeting.supplySource.distributionFilter.reset'
          })}
          type="secondary"
        />
      </div>
      <TaptapList className="adExchangeDistribution__table" data={tableRows} id={'adExchangeDistribution-list'}>
        <TaptapList.ColumnsBar>
          <TaptapList.ColumnsBar.Column
            description={formatMessage({
              defaultMessage: 'AdEx',
              id: 'targeting.supplySource.distributionTable.adName'
            })}
            icon={getSortIcon('name')}
            id={'name'}
            onIconClick={() => handleSort('name')}
          />
          <TaptapList.ColumnsBar.Column
            description={formatMessage({
              defaultMessage: 'Weight',
              id: 'targeting.supplySource.distributionTable.weight'
            })}
            icon={getSortIcon('distribution')}
            id={'distribution'}
            onIconClick={() => handleSort('distribution')}
          />
          {goalsTotal.map(goal => (
            <React.Fragment key={goal.name}>
              {Boolean(goal.overall) && (
                <TaptapList.ColumnsBar.Column
                  className="adExchangeDistribution__tableGoalColumn"
                  description={formatMessage({
                    defaultMessage: `${capitalize(goal.name)} (Overall)`,
                    id: `targeting.supplySource.distributionTable.${goal.name}_overall`
                  })}
                  icon={getSortIcon(`${goal.name}_overall`)}
                  id={`${goal.name}_overall`}
                  onIconClick={() => handleSort(`${goal.name}_overall`)}
                />
              )}
              {Boolean(goal.daily) && (
                <TaptapList.ColumnsBar.Column
                  className="adExchangeDistribution__tableGoalColumn"
                  description={formatMessage({
                    defaultMessage: `${capitalize(goal.name)} (Daily)`,
                    id: `targeting.supplySource.distributionTable.${goal.name}_daily`
                  })}
                  icon={getSortIcon(`${goal.name}_daily`)}
                  id={`${goal.name}_daily`}
                  onIconClick={() => handleSort(`${goal.name}_daily`)}
                />
              )}
            </React.Fragment>
          ))}
        </TaptapList.ColumnsBar>
        <TaptapList.RowsList<TableData>>
          {items =>
            items.map(ad => (
              <TaptapList.RowsList.Row id={ad.id} key={ad.id}>
                <div className="adExchangeDistribution__tableCellName">
                  <span className="adExchangeDistribution__tableCellNameText">{ad.name}</span>
                  <Range
                    className="adExchangeDistribution__tableFieldNameRange"
                    id={`${ad.name}-distributionRange`}
                    key={`${ad.id}-${ad.distribution}`}
                    max={100}
                    min={0}
                    onChange={() => {
                      // FIX: Range onChange should be optional
                    }}
                    type="slider"
                    value={ad.distribution}
                  >
                    <Range.Bar
                      onAfterChange={(value: number | number[]) =>
                        handleDistribution(ad.id)(Array.isArray(value) ? value[0] : value)
                      }
                    />
                  </Range>
                </div>
                <div className="adExchangeDistribution__tableCellWeight">
                  <Input
                    className="adExchangeDistribution__tableFieldWeightInput"
                    id={`${ad.name}-distribution`}
                    inputAdditionalProps={{ max: 100, min: 0 }}
                    name={`${ad.name}-distribution`}
                    notFloatingPlaceholder
                    numberFormat="percentage"
                    onChange={value => handleDistribution(ad.id)(parseInt(value as string) || 0)}
                    shape="squareUI"
                    tiny
                    type="number"
                    value={ad.distribution}
                  />
                </div>
                {goalsTotal.map(goal => (
                  <React.Fragment key={goal.name}>
                    {Boolean(goal.overall) && (
                      <div className="adExchangeDistribution__tableCellGoal">
                        {normalizeWithUnit(ad[`${goal.name}_overall`], baseCurrency)}
                      </div>
                    )}
                    {Boolean(goal.daily) && (
                      <div className="adExchangeDistribution__tableCellGoal">
                        {normalizeWithUnit(ad[`${goal.name}_daily`], baseCurrency)}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </TaptapList.RowsList.Row>
            ))
          }
        </TaptapList.RowsList>
      </TaptapList>
      <div className="adExchangeDistribution__tableTotalRow">
        <div>{formatMessage({ defaultMessage: 'TOTAL', id: 'targeting.supplySource.distributionTable.total' })}</div>
        <div className="adExchangeDistribution__tableTotalRowDistribution">
          {tableRows.reduce((prev, cur) => prev + cur.distribution, 0)}%
        </div>
        {goalsTotal.map(goal => (
          <React.Fragment key={goal.name}>
            {Boolean(goal.overall) && (
              <div className="adExchangeDistribution__tableTotalRowGoal">
                {normalizeWithUnit(
                  tableRows.reduce((prev, cur) => prev + cur[`${goal.name}_overall`], 0),
                  baseCurrency
                )}
              </div>
            )}
            {Boolean(goal.daily) && (
              <div className="adExchangeDistribution__tableTotalRowGoal">
                {normalizeWithUnit(
                  tableRows.reduce((prev, cur) => prev + cur[`${goal.name}_daily`], 0),
                  baseCurrency
                )}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
